import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { boilingApi, spindleApi } from '../../../shared/lib/api'
import { useAuthStore } from '../../../shared/store/authStore'
import TabletHeader from '../../../shared/components/TabletHeader'
import TabletScreen from '../../../shared/components/TabletScreen'
import { FlowNote, InfoCard, BigButton, ScanZone, SuccessScreen, Divider, LoadingSpinner } from '../../../shared/components/UI'

export default function BoilingPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  
  const [containerInput, setContainerInput] = useState('')
  const [outputContext, setOutputContext] = useState(null)
  const [loadingContext, setLoadingContext] = useState(false)
  
  const [done, setDone] = useState(false)
  const [skipped, setSkipped] = useState(false)
  const [error, setError] = useState('')

  const handleScan = () => {
    if (!containerInput.trim()) return
    setLoadingContext(true)
    setError('')
    spindleApi.scanOutput(containerInput.trim())
      .then(res => setOutputContext(res.data.data))
      .catch(err => setError(err.response?.data?.message || 'No active spindle output found for this container'))
      .finally(() => setLoadingContext(false))
  }

  const { mutate: confirmMutate, isPending: isConfirming } = useMutation({
    mutationFn: () => boilingApi.confirm({ 
      spindleOutputId: outputContext.id, 
      containerId: outputContext.output_container_id 
    }),
    onSuccess:  () => setDone(true),
    onError: (err) => setError(err.response?.data?.message || 'Failed to confirm boiling')
  })

  const { mutate: skipMutate, isPending: isSkipping } = useMutation({
    mutationFn: () => boilingApi.skip({ 
      spindleOutputId: outputContext.id, 
      containerId: outputContext.output_container_id 
    }),
    onSuccess:  () => setSkipped(true),
    onError: (err) => setError(err.response?.data?.message || 'Failed to skip boiling')
  })

  if (done || skipped) return (
    <TabletScreen>
      <TabletHeader title="Boiling" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">
        <SuccessScreen
          title={done ? 'Boiling confirmed' : 'Boiling skipped'}
          subtitle={done ? `Container ${outputContext?.loading?.batch?.container_id} · Batch ${outputContext?.loading?.batch?.batch_number}` : 'Proceeding to Cone Formation'}
        />
        <BigButton variant="primary" onClick={() => navigate('/app/cone')}>Proceed to Cone Formation</BigButton>
        <BigButton variant="secondary" onClick={() => navigate('/app/dashboard')}>Back to Dashboard</BigButton>
      </div>
    </TabletScreen>
  )

  return (
    <TabletScreen>
      <TabletHeader title="Boiling" role="Loading Supervisor" unit={user?.unitName || 'Unit'} teal />
      <div className="p-4">

        {!outputContext ? (
          <>
            <ScanZone teal title="Scan Container QR" subtitle="Scan the container coming from Spindle run" />
            <div className="flex gap-2 mb-5">
              <input
                className="form-input flex-1 text-sm"
                placeholder="Or type container ID"
                value={containerInput}
                onChange={(e) => setContainerInput(e.target.value)}
              />
              <button
                disabled={loadingContext}
                onClick={handleScan}
                className="bg-teal-600 text-white px-4 rounded-lg text-sm font-medium hover:bg-teal-700"
              >
                {loadingContext ? '...' : 'Load'}
              </button>
            </div>
            {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
            <Divider />
            <BigButton variant="secondary" onClick={() => navigate('/app/dashboard')} className="mt-2 text-sm">Cancel</BigButton>
          </>
        ) : (
          <>
            <FlowNote variant="teal">Spindle Output Found · Batch {outputContext.loading?.batch?.batch_number}</FlowNote>
            <InfoCard rows={[
              { label: 'Container',     value: outputContext.loading?.batch?.container_id, valueVariant: 'blue' },
              { label: 'Batch',         value: outputContext.loading?.batch?.batch_number },
              { label: 'Shade',         value: outputContext.loading?.batch?.shade?.name },
              { label: 'Output weight', value: `${outputContext.actual_output_kg} KG` },
            ]} className="mb-6" />
            
            {error && <p className="text-red-500 text-xs mb-3">{error}</p>}

            <div className="flex flex-col gap-3">
              <BigButton
                variant="success"
                onClick={() => confirmMutate()}
                disabled={isConfirming || isSkipping}
                className="py-6 text-xl"
              >
                {isConfirming ? <LoadingSpinner /> : 'Confirm Boiling Done'}
              </BigButton>
              
              <BigButton
                variant="secondary"
                onClick={() => skipMutate()}
                disabled={isConfirming || isSkipping}
              >
                {isSkipping ? 'Skipping...' : 'Skip Boiling'}
              </BigButton>
            </div>

            <BigButton
              variant="secondary"
              onClick={() => setOutputContext(null)}
              className="mt-4"
            >
              Back
            </BigButton>
          </>
        )}
      </div>
    </TabletScreen>
  )
}
